import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { UserLanguageSync } from '../auth/UserLanguageSync';

import { useLanguage } from '../../hooks/useLanguage';
import { isAuthenticated } from '../../utils/tokenManager';

import { Footer } from './Footer';
import { Header } from './Header';
import { Sidebar } from './Sidebar';

interface LayoutProps {
  children: React.ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true); // Default open on desktop
  const [isDesktop, setIsDesktop] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const { isRTL } = useLanguage();
  const location = useLocation();
  const auth = isAuthenticated();

  // When sidebar closes on mobile, return focus to menu button so focus is not inside inert/hidden content
  useEffect(() => {
    if (!isSidebarOpen && !isDesktop) {
      menuButtonRef.current?.focus({ preventScroll: true });
    }
  }, [isSidebarOpen, isDesktop]);

  // Initialize and handle screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      
      // On mobile, always close sidebar initially
      if (!desktop) {
        setIsSidebarOpen(false);
      }
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Close sidebar on route change (mobile/tablet only - NOT desktop)
  useEffect(() => {
    if (!isDesktop) {
      setIsSidebarOpen(false);
    }
    // On desktop, keep sidebar state - don't close on route change
  }, [location.pathname, isDesktop]);

  const handleMenuToggle = () => {
    setIsSidebarOpen((prev) => !prev);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  return (
    <div
      className="min-h-screen flex flex-col overflow-x-hidden"
      style={{ backgroundColor: location.pathname === '/login' ? '#0E4A4A' : 'var(--brand-primary)' }}
    >
      {auth ? <UserLanguageSync /> : null}
      {location.pathname !== '/login' && (
        <Header
          onMenuToggle={auth ? handleMenuToggle : undefined}
          isMenuOpen={isSidebarOpen}
          menuButtonRef={auth ? menuButtonRef : undefined}
        />
      )}

      <div className={`flex flex-1 relative overflow-x-hidden ${location.pathname !== '/login' ? 'pt-20 md:pt-24' : ''}`}>
        {/* Sidebar - only show if authenticated */}
        {auth && <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />}

        {/* Main Content */}
        <main
          className={`flex-1 transition-all duration-300 min-w-0 ${
            auth && isSidebarOpen ? (isRTL ? 'lg:mr-64' : 'lg:ml-64') : ''
          }`}
          role="main"
        >
          <div className={`${auth && isSidebarOpen ? 'max-w-7xl' : 'max-w-6xl'} mx-auto ${location.pathname !== '/login' ? 'px-4 py-6 md:py-8' : 'p-0'}`}>
            {children}
          </div>
        </main>
      </div>

      {/* Footer - only show for non-authenticated users */}
      {!auth && location.pathname !== '/login' && <Footer />}
    </div>
  );
}

